import { execSync, execFileSync } from 'node:child_process'
import { rm, copyFile } from 'node:fs/promises'
import { existsSync } from 'node:fs'
import { join } from 'node:path'

const GLOB_RE = /[*?[]/

/**
 * Reject a tarball whose entries could escape the extraction directory
 * (absolute paths or `..` traversal). Throws on the first unsafe entry so
 * callers never extract it.
 */
export function assertSafeTarball(tarballPath: string): void {
  const listing = execSync(`tar tzf "${tarballPath}"`, { stdio: 'pipe' }).toString()
  const unsafe = listing.split('\n').find((p) => p.startsWith('/') || p.split('/').includes('..'))
  if (unsafe) {
    throw new Error(`Unsafe tarball entry rejected: ${unsafe}`)
  }
}

/** Result of running a setup command. */
export interface SetupResult {
  ok: boolean
  stderr: string
}

/**
 * Run a scenario setup command (e.g. `pnpm install`) in the workspace.
 * Setup commands are authored in scenario YAML and are trusted by design,
 * so they execute through the shell.
 */
export function execSetup(cmd: string, cwd: string): SetupResult {
  try {
    execSync(cmd, { cwd, stdio: 'pipe', timeout: 300000 })
    return { ok: true, stderr: '' }
  } catch (err) {
    const stderr = err instanceof Error ? err.message : String(err)
    let hint = ''
    if (stderr.includes('command not found') || stderr.includes('ENOENT')) {
      hint = 'Is the required package manager installed and on PATH?'
    } else if (stderr.includes('permission denied') || stderr.includes('EACCES')) {
      hint = 'Try running with appropriate permissions or in a writable directory.'
    } else if (stderr.includes('MODULE_NOT_FOUND') || stderr.includes('Cannot find module')) {
      hint = 'A package dependency is missing. Try reinstalling.'
    }
    return { ok: false, stderr: hint ? `${stderr}\n  Hint: ${hint}` : stderr }
  }
}

/**
 * Expand a glob pattern into matching absolute paths.
 *
 * The pattern is passed as a positional shell argument (`$@`) rather than
 * interpolated into the `-c` script, so the shell only performs pathname
 * expansion — never command substitution. This avoids both the injection
 * risk of string-interpolating the pattern and the "glob doesn't expand"
 * bug caused by quoting it.
 */
function expandGlob(pattern: string): string[] {
  // `IFS=` (empty) disables field splitting so paths containing spaces survive,
  // while the unquoted `$@` still lets the shell perform pathname (glob)
  // expansion. The pattern is a positional argument, not part of the script,
  // so its contents are never re-evaluated as commands (no `$(...)` injection).
  const out = execFileSync('sh', ['-c', 'IFS=; printf "%s\\n" $@', 'sh', pattern], {
    stdio: ['ignore', 'pipe', 'ignore'],
    encoding: 'utf-8',
  })
  return out.split('\n').filter(Boolean)
}

/**
 * Resolve the on-disk directory that holds the `@tonty/scenarios` package
 * (`registry.yaml` + per-scenario dirs), or null when it can't be found.
 *
 * Order:
 *   1. `@tonty/scenarios` via its `getScenariosDir()` — validated by the
 *      presence of `registry.yaml`, since a bundled copy (e.g. the CLI's
 *      `dist/` chunk) reports its own directory which contains no scenarios.
 *   2. `<cwd>/packages/scenarios` — a monorepo checkout (the interviewer
 *      runs `host`/`start` from the repo root).
 */
export async function resolveScenariosDir(): Promise<string | null> {
  try {
    const { getScenariosDir } = await import('@tonty/scenarios')
    const dir = getScenariosDir()
    if (existsSync(join(dir, 'registry.yaml'))) return dir
  } catch {
    // @tonty/scenarios not resolvable — fall through to CWD lookup
  }

  const fromCwd = join(process.cwd(), 'packages', 'scenarios')
  if (existsSync(join(fromCwd, 'registry.yaml'))) return fromCwd

  return null
}

/**
 * Copy a scenario's `verify.sh` into the workspace so hosted/joined sessions
 * can score the result. The candidate's installed CLI ships no local copy of
 * the scenarios package, so the workspace is the only reliable location.
 * Best-effort: a missing script is silently skipped.
 */
export async function copyVerifyScript(workdir: string, scenarioName: string): Promise<void> {
  const dir = await resolveScenariosDir()
  if (!dir) return
  const src = join(dir, scenarioName, 'verify.sh')
  if (existsSync(src)) {
    await copyFile(src, join(workdir, 'verify.sh')).catch(() => {})
  }
}

/**
 * Delete files/directories under `baseDir`. Entries may be plain paths or
 * glob patterns (e.g. `src/*.test.ts`). Globs are expanded safely without
 * shell-interpolating the pattern. Best-effort: missing targets are ignored.
 */
export async function deleteMatchingFiles(baseDir: string, patterns: string[]): Promise<void> {
  for (const pattern of patterns) {
    if (GLOB_RE.test(pattern)) {
      for (const match of expandGlob(join(baseDir, pattern))) {
        await rm(match, { recursive: true, force: true }).catch(() => {})
      }
    } else {
      await rm(join(baseDir, pattern), { recursive: true, force: true }).catch(() => {})
    }
  }
}
